<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination as SwiperPaginationModule } from 'swiper/modules';
import * as XLSX from 'xlsx';

import apiClient from "@/axios";

// --- Data for existing Swiper and Contract List ---
const contractData = ref([]);
const personalData = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(5);

// --- State for Personnel Project Profitability Table ---
const selectedPersonal = ref(null);
const projectProfitabilityData = ref([]);
const isLoadingProfitabilityData = ref(false);
const personalSearchQuery = ref(''); // Personel arama sorgusu için state
const isPersonalDropdownOpen = ref(false); // Personel dropdown'ının açık olup olmadığını kontrol eder
const sortColumnPersonal = ref(null); // Sıralama yapılan sütun
const sortDirectionPersonal = ref('asc'); // Sıralama yönü: 'asc' veya 'desc'

// --- State for Company Project Profitability Table ---
const companiesList = ref([]);
const selectedCompanyForProfitability = ref(null);
const companyProfitabilityData = ref([]);
const isLoadingCompanyProfitability = ref(false);
const companySearchQuery = ref(''); // Firma arama sorgusu için state
const isCompanyDropdownOpen = ref(false); // Firma dropdown'ının açık olup olmadığını kontrol eder
const sortColumnCompany = ref(null); // Sıralama yapılan sütun
const sortDirectionCompany = ref('asc'); // Sıralama yönü: 'asc' veya 'desc'

// --- State for Company Profitability Summary Table ---
const currentPageCompanyProfitability = ref(1);
const itemsPerPageCompanyProfitability = ref(5);
const sortColumnCompanyRates = ref(null); // Sıralama yapılan sütun: 'profit_after_admin_percentage' veya 'total_profit_after_admin'
const sortDirectionCompanyRates = ref(null); // Sıralama yönü: null (normal), 'desc' (yüksekten düşüğe), 'asc' (düşükten yükseğe)

// --- State for Dashboard Summary Data ---
const activeContractsAmount = ref(0);
const latestManagementExpense = ref(null);

// --- Helper Formatters ---
const formatDate = (timestamp, allowNull = false) => {
  if (!timestamp) return allowNull ? "" : "Bilgi Yok";
  return new Date(timestamp).toLocaleDateString("tr-TR", {
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
};

const formatCurrency = (value) => {
  if (value === null || typeof value === 'undefined') return "₺0,00";
  const num = parseFloat(value);
  if (isNaN(num)) return "₺0,00";
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(num);
};

const formatBoolean = (value) => {
  if (value === null || typeof value === 'undefined') return "Bilgi Yok";
  if (value === true) return "Evet";
  if (value === false) return "Hayır";
  return "Geçersiz Değer";
};

const normalizeSearch = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).toLocaleLowerCase('tr-TR');
};

// --- Table Headers Definitions ---
const tableHeadersPersonal = ref([
  // PostgreSQL'den gelen temel bilgiler
  { key: 'personalname', label: 'Personel Adı' },
  { key: 'companyname', label: 'Firma Adı' },
  { key: 'contractname', label: 'Sözleşme Adı' },
  { key: 'contractnumber', label: 'Sözleşme No' },
  { key: 'contractdate', label: 'Söz. Baş. Tarihi', formatter: formatDate },
  { key: 'renewalcontractdate', label: 'Söz. Yen. Tarihi', formatter: formatDate },
  { key: 'personalstartdate', label: 'Per. Proje Başlama Tarihi', formatter: formatDate },
  { key: 'personalenddate', label: 'Per. Proje Ayrılma Tarihi', formatter: (val) => formatDate(val, true) },
  // MongoDB'dan gelen tüm diğer bilgiler
  { key: 'contracttime', label: 'Sözleşme Süresi (Ay)' },
  { key: 'contractprice', label: 'Sözleşme Bedeli', formatter: formatCurrency, isSummable: true },
  { key: 'hoursnetamount', label: 'Saatlik Brüt Tutar', formatter: formatCurrency },
  { key: 'monthGrossAmount', label: 'Aylık Brüt Tutar', formatter: formatCurrency, isSummable: true },
  { key: 'workedhours', label: 'Çalışılan Saat', isSummable: true, formatter: val => val !== null && val !== undefined ? parseFloat(val).toFixed(2) : '0.00' },
  { key: 'insuranceDay', label: 'Sigorta Günü', isSummable: true },
  { key: 'sgkEmployerShare', label: 'SGK İşveren Payı', formatter: formatCurrency, isSummable: true },
  { key: 'sgkrate', label: 'SGK Oranı', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'fuelexpenses', label: 'Yakıt Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'travelexpenses', label: 'Seyahat Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'foodexpenses', label: 'Yemek Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'carrentexpenses', label: 'Araç Kira Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'otherexpenses', label: 'Diğer Giderler', formatter: formatCurrency, isSummable: true },
  { key: 'replacementservice', label: 'Yedek Hizmet', formatter: formatBoolean },
  { key: 'seniorityburden', label: 'Kıdem Yükü', formatter: formatBoolean },
  { key: 'severanceAmount', label: 'Kıdem Taz. Tutarı', formatter: formatCurrency, isSummable: true },
  { key: 'totalOfExpenses', label: 'Toplam Masraflar', formatter: formatCurrency, isSummable: true },
  { key: 'totalCost', label: 'Toplam Maliyet', formatter: formatCurrency, isSummable: true },
  { key: 'invoiceAmount', label: 'Fatura Tutarı', formatter: formatCurrency, isSummable: true },
  { key: 'profitBeforeAdministrativeExpenses', label: 'Yönetim Gid. Öncesi Kâr/Zarar', formatter: formatCurrency, isSummable: true },
  { key: 'profitBeforeManagementRate', label: 'Yönetim Gid. Öncesi Kar Oranı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'managementExpenseRateTL', label: 'Yönetim Gider Payı (TL)', formatter: formatCurrency, isSummable: true },
  { key: 'projectProfitabilityManagementExpenseRate', label: 'Yönetim Gider Payı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'profitAfterAdministrativeExpenses', label: 'Yönetim Gid. Sonrası Kâr/Zarar', formatter: formatCurrency, isSummable: true },
  { key: 'profitAfterManagementRate', label: 'Yönetim Gid. Sonrası Kar Oranı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'isActive', label: 'Aktif Durum', formatter: formatBoolean },
]);
const tableHeadersCompany = ref([
  { key: 'company_name', label: 'Firma Adı' },
  { key: 'contract_name', label: 'Sözleşme Adı' },
  { key: 'personal_name', label: 'Personel Adı' },
  { key: 'contract_number', label: 'Sözleşme No' },
  { key: 'contract_date', label: 'Söz. Baş. Tarihi', formatter: formatDate },
  { key: 'renewal_contract_date', label: 'Söz. Yen. Tarihi', formatter: formatDate },
  { key: 'worked_hours', label: 'Toplam Çalışılan Saat', isSummable: true, formatter: val => val !== null && val !== undefined ? parseFloat(val).toFixed(2) : 'Bilgi Yok' },
  { key: 'contract_time', label: 'Sözleşme Süresi (Ay)' },
  { key: 'projectprofitabilityinsuranceday', label: 'Sigorta Günü', isSummable: true },
  { key: 'projectprofitabilityinvoiceamount', label: 'Fatura Tutarı', formatter: formatCurrency, isSummable: true },
  { key: 'contract_price', label: 'Sözleşme Bedeli', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilitymonthgrossamount', label: 'Aylık Brüt Tutar', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilitysgkemployershare', label: 'SGK İşveren Payı', formatter: formatCurrency, isSummable: true },
  { key: 'insurance_base', label: 'Sigorta Tabanı', formatter: formatCurrency },
  { key: 'projectprofitabilityseveranceamount', label: 'Kıdem Taz. Tutarı', formatter: formatCurrency, isSummable: true },
  { key: 'fuel_expenses', label: 'Yakıt Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'travel_expenses', label: 'Seyahat Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'food_expenses', label: 'Yemek Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'car_rent_expenses', label: 'Araç Kira Gideri', formatter: formatCurrency, isSummable: true },
  { key: 'other_expenses', label: 'Diğer Giderler', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilitytotalofexpenses', label: 'Toplam Masraflar', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilitytotalcost', label: 'Toplam Maliyet', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilityprofitbeforeadministrativeexpenses', label: 'Yönetim Gid. Öncesi Kâr/Zarar', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilityprofitbeforemanagementrate', label: 'Yönetim Gid. Öncesi Kar Oranı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'projectprofitabilitymanagementexpenseratetl', label: 'Yönetim Gider Payı (TL)', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilitymanagementexpenserate', label: 'Yönetim Gider Payı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'projectprofitabilityprofitafteradministrativeexpenses', label: 'Yönetim Gid. Sonrası Kâr/Zarar', formatter: formatCurrency, isSummable: true },
  { key: 'projectprofitabilityprofitaftermanagementrate', label: 'Yönetim Gid. Sonrası Kar Oranı (%)', formatter: val => val ? `%${parseFloat(val).toFixed(2)}` : '%0.00' },
  { key: 'replacement_service', label: 'Yedek Hizmet', formatter: formatBoolean },
  { key: 'seniority_burden', label: 'Kıdem Yükü', formatter: formatBoolean },
  { key: 'company_registration_number', label: 'Firma Sicil No'},
  { key: 'is_subcontractor', label: 'Alt Yüklenici', formatter: formatBoolean },
]);

// Personel listesini arama sorgusuna göre filtreler
const filteredPersonalData = computed(() => {
  const source = personalData.value || [];
  const query = normalizeSearch(personalSearchQuery.value);
  if (!query) {
    return source;
  }
  return source.filter(person =>
      normalizeSearch(person?.name).includes(query)
  );
});

// Firma listesini arama sorgusuna göre filtreler
const filteredCompaniesList = computed(() => {
  const source = companiesList.value || [];
  const query = normalizeSearch(companySearchQuery.value);
  if (!query) {
    return source;
  }
  return source.filter(company =>
      normalizeSearch(company?.name).includes(query)
  );
});

// --- Dropdown Seçim Fonksiyonları ---
function selectPersonal(person) {
  selectedPersonal.value = person;
  // Seçim yapıldıktan sonra input alanını temizle
  personalSearchQuery.value = '';
  isPersonalDropdownOpen.value = false; // Dropdown'ı kapat
}

function selectCompany(company) {
  selectedCompanyForProfitability.value = company;
  // Seçim yapıldıktan sonra input alanını temizle
  companySearchQuery.value = '';
  isCompanyDropdownOpen.value = false; // Dropdown'ı kapat
}

function closeDropdown(type) {
  setTimeout(() => {
    if (type === 'personal') isPersonalDropdownOpen.value = false;
    if (type === 'company') isCompanyDropdownOpen.value = false;
  }, 200);
}

// Generic totals calculation function
const calculateTotals = (data, headers) => {
  if (!data || !data.length) return null;
  const totals = {};
  headers.forEach(header => { if (header.isSummable) { totals[header.key] = 0; } });
  data.forEach(item => {
    headers.forEach(header => {
      if (header.isSummable && item[header.key] !== null && typeof item[header.key] !== 'undefined' && !isNaN(parseFloat(item[header.key]))) {
        totals[header.key] += parseFloat(item[header.key]);
      }
    });
  });
  if (headers.length > 0) { totals[headers[0].key] = 'TOPLAM'; }
  return totals;
};

// Sıralanmış personel karlılık verisi
const sortedProjectProfitabilityData = computed(() => {
  if (!sortColumnPersonal.value || !projectProfitabilityData.value.length) {
    return projectProfitabilityData.value;
  }
  
  const sorted = [...projectProfitabilityData.value].sort((a, b) => {
    const aValue = a[sortColumnPersonal.value];
    const bValue = b[sortColumnPersonal.value];
    
    // Null/undefined kontrolü
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    // Sayısal değer kontrolü
    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      // Sayısal karşılaştırma
      return sortDirectionPersonal.value === 'asc' ? aNum - bNum : bNum - aNum;
    }
    
    // String karşılaştırma
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    
    if (sortDirectionPersonal.value === 'asc') {
      return aStr.localeCompare(bStr, 'tr');
    } else {
      return bStr.localeCompare(aStr, 'tr');
    }
  });
  
  return sorted;
});

const personnelProfitabilityTotals = computed(() => calculateTotals(sortedProjectProfitabilityData.value, tableHeadersPersonal.value));

// Sıralanmış firma karlılık verisi
const sortedCompanyProfitabilityData = computed(() => {
  if (!sortColumnCompany.value || !companyProfitabilityData.value.length) {
    return companyProfitabilityData.value;
  }
  
  const sorted = [...companyProfitabilityData.value].sort((a, b) => {
    const aValue = a[sortColumnCompany.value];
    const bValue = b[sortColumnCompany.value];
    
    // Null/undefined kontrolü
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    // Sayısal değer kontrolü
    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      // Sayısal karşılaştırma
      return sortDirectionCompany.value === 'asc' ? aNum - bNum : bNum - aNum;
    }
    
    // String karşılaştırma
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    
    if (sortDirectionCompany.value === 'asc') {
      return aStr.localeCompare(bStr, 'tr');
    } else {
      return bStr.localeCompare(aStr, 'tr');
    }
  });
  
  return sorted;
});

const companyProfitabilityTotals = computed(() => calculateTotals(sortedCompanyProfitabilityData.value, tableHeadersCompany.value));

const summaryData = ref({});
const totalCostData = ref({});
const totalProfitsData = ref({ totalProfitBefore: 0, totalProfitAfter: 0, recordCount: 0 });
const averageProfitsData = ref({ averageProfitRateBeforeManagement: 0, averageProfitRateAfterManagement: 0, recordCount: 0 });
const profitBeforeManagementAmount = computed(() => {
  const activeAmount = Number(activeContractsAmount.value) || 0;
  const totalCost = Number(totalCostData.value && totalCostData.value.totalCost) || 0;
  return activeAmount - totalCost;
});

const profitBeforeManagementRate = computed(() => {
  const profitAmount = profitBeforeManagementAmount.value;
  const activeAmount = Number(activeContractsAmount.value) || 0;
  if (activeAmount === 0) return 0;
  return profitAmount / activeAmount;
});

const profitAfterManagementAmount = computed(() => {
  const activeAmount = Number(activeContractsAmount.value) || 0;
  const totalCost = Number(totalCostData.value && totalCostData.value.totalCost) || 0;
  const managementExpense = Number(latestManagementExpense.value && latestManagementExpense.value.companyManagementExpense) || 0;
  return activeAmount - totalCost - managementExpense;
});

const profitAfterManagementRate = computed(() => {
  const profitAmount = profitAfterManagementAmount.value;
  const activeAmount = Number(activeContractsAmount.value) || 0;
  if (activeAmount === 0) return 0;
  return profitAmount / activeAmount;
});

const fetchCompanyProfitabilitySummary = async () => {
  try {
    const response = await apiClient.get('/contract/company-profitability/summary');
    summaryData.value = response.data;
  } catch (error) { console.error('Özet veriler alınırken hata:', error); }
};

const fetchTotalCostFromMongoDB = async () => {
  try {
    const response = await apiClient.get('/contract/total-cost');
    totalCostData.value = response.data;
  } catch (error) {
    console.error('MongoDB toplam maliyet verisi alınırken hata:', error);
    totalCostData.value = { totalCost: 0, recordCount: 0 };
  }
};

const fetchTotalProfitsFromMongoDB = async () => {
  try {
    const response = await apiClient.get('/contract/total-profits');
    totalProfitsData.value = response.data;
  } catch (error) {
    console.error('MongoDB kar verisi alınırken hata:', error);
    totalProfitsData.value = { totalProfitBefore: 0, totalProfitAfter: 0, recordCount: 0 };
  }
};

const fetchActiveContractsAmount = async () => {
  try {
    const response = await apiClient.get('/contract/total-valid-price');
    activeContractsAmount.value = response.data;
  } catch (error) {
    console.error('Aktif sözleşme tutarı alınırken hata:', error);
    activeContractsAmount.value = 0;
  }
};

const fetchLatestManagementExpense = async () => {
  try {
    const response = await apiClient.get('/companyManagementExpense/latest');
    latestManagementExpense.value = response.data;
  } catch (error) {
    console.error('Yönetim gideri alınırken hata:', error);
    latestManagementExpense.value = null;
  }
};

const fetchAverageProfitsFromMongoDB = async () => {
  try {
    const response = await apiClient.get('/contract/average-profits');
    averageProfitsData.value = response.data;
  } catch (error) {
    console.error('MongoDB ortalama kar verisi alınırken hata:', error);
    averageProfitsData.value = { averageProfitRateBeforeManagement: 0, averageProfitRateAfterManagement: 0, recordCount: 0 };
  }
};

const summaryDataAsc = ref([]);
const fetchCompanyProfitabilitySummaryAsc = async () => {
  try {
    const response = await apiClient.get('/contract/company-profitability/summaryAsc');
    summaryDataAsc.value = Array.isArray(response.data) ? response.data : [response.data];
  } catch (error) {
    console.error('Özet veriler alınırken hata:', error);
    summaryDataAsc.value = [];
  }
};

const getProfitabilityClass = (percentage) => {
  if (percentage < 30) return 'low-profit';
  if (percentage >= 30 && percentage <= 50) return 'medium-profit';
  return 'high-profit';
};

const formatPercentage = (value) => value ? `${value.toFixed(2)}%` : '0%';

// --- Company Profitability Summary Pagination Computed Properties ---
const paginatedCompanyProfitability = computed(() => {
  let sortedData = [...summaryDataAsc.value];
  
  // Sıralama varsa uygula
  if (sortColumnCompanyRates.value && sortDirectionCompanyRates.value) {
    sortedData.sort((a, b) => {
      let aValue, bValue;
      
      if (sortColumnCompanyRates.value === 'profit_after_admin_percentage') {
        aValue = parseFloat(a.profit_after_admin_percentage) || 0;
        bValue = parseFloat(b.profit_after_admin_percentage) || 0;
      } else if (sortColumnCompanyRates.value === 'total_profit_after_admin') {
        aValue = parseFloat(a.total_profit_after_admin) || 0;
        bValue = parseFloat(b.total_profit_after_admin) || 0;
      } else {
        return 0;
      }
      
      if (sortDirectionCompanyRates.value === 'desc') {
        return bValue - aValue; // Yüksekten düşüğe
      } else if (sortDirectionCompanyRates.value === 'asc') {
        return aValue - bValue; // Düşükten yükseğe
      }
      
      return 0;
    });
  } else {
    // Sıralama yoksa varsayılan olarak karlılık oranına göre yüksekten düşüğe
    sortedData.sort((a, b) => {
      const aPercentage = parseFloat(a.profit_after_admin_percentage) || 0;
      const bPercentage = parseFloat(b.profit_after_admin_percentage) || 0;
      return bPercentage - aPercentage; // Yüksekten düşüğe
    });
  }

  const startIndex = (currentPageCompanyProfitability.value - 1) * itemsPerPageCompanyProfitability.value;
  const endIndex = startIndex + itemsPerPageCompanyProfitability.value;
  return sortedData.slice(startIndex, endIndex);
});

const totalPagesCompanyProfitability = computed(() => {
  return Math.ceil(summaryDataAsc.value.length / itemsPerPageCompanyProfitability.value);
});

const hasNextPageCompanyProfitability = computed(() => {
  return currentPageCompanyProfitability.value < totalPagesCompanyProfitability.value;
});

const hasPrevPageCompanyProfitability = computed(() => {
  return currentPageCompanyProfitability.value > 1;
});

// --- Company Profitability Rates Sorting Functions ---
const sortCompanyRatesTable = (column) => {
  // Sadece 'profit_after_admin_percentage' ve 'total_profit_after_admin' sütunları için sıralama yap
  if (column !== 'profit_after_admin_percentage' && column !== 'total_profit_after_admin') {
    return;
  }
  
  // Aynı sütuna tıklanırsa sıralama yönünü değiştir
  if (sortColumnCompanyRates.value === column) {
    if (sortDirectionCompanyRates.value === null) {
      sortDirectionCompanyRates.value = 'desc'; // İlk tıklama: Yüksekten düşüğe
    } else if (sortDirectionCompanyRates.value === 'desc') {
      sortDirectionCompanyRates.value = 'asc'; // İkinci tıklama: Düşükten yükseğe
    } else if (sortDirectionCompanyRates.value === 'asc') {
      sortColumnCompanyRates.value = null; // Üçüncü tıklama: Normal (sıralama yok)
      sortDirectionCompanyRates.value = null;
    }
  } else {
    // Farklı sütuna tıklanırsa yeni sütunu seç ve yüksekten düşüğe başlat
    sortColumnCompanyRates.value = column;
    sortDirectionCompanyRates.value = 'desc';
  }
  
  // Sayfa numarasını sıfırla
  currentPageCompanyProfitability.value = 1;
};

const getSortIconCompanyRates = (column) => {
  if (sortColumnCompanyRates.value !== column) {
    return ''; // Sıralama yok
  }
  
  if (sortDirectionCompanyRates.value === 'desc') {
    return '▼'; // Yüksekten düşüğe
  } else if (sortDirectionCompanyRates.value === 'asc') {
    return '▲'; // Düşükten yükseğe
  }
  
  return ''; // Normal (sıralama yok)
};

onMounted(async () => {
  try {
    await Promise.allSettled([
      getContractData(),
      getPersonelData(),
      getCompaniesList(),
      fetchCompanyProfitabilitySummary(),
      fetchCompanyProfitabilitySummaryAsc(),
      fetchTotalCostFromMongoDB(),
      fetchTotalProfitsFromMongoDB(),
      fetchAverageProfitsFromMongoDB(),
      fetchActiveContractsAmount(),
      fetchLatestManagementExpense()
    ]);

    // Sayfa numaralarını sıfırla
    currentPageCompanyProfitability.value = 1;

    // Global event listener ekle - personel gider güncellendiğinde dashboard'ı yenile
    window.addEventListener('personal-expense-updated', async () => {
      try {
        await fetchActiveContractsAmount();
        await fetchCompanyProfitabilitySummary();
        await fetchCompanyProfitabilitySummaryAsc();
      } catch (error) {
        console.error('Event listener hatası:', error);
      }
    });
  } catch (error) {
    console.error('Dashboard yüklenirken hata:', error);
  }
});

watch(selectedPersonal, async (newValue) => {
  if (!newValue) {
    projectProfitabilityData.value = [];
  } else if (newValue.id === 'all') {
    await fetchProjectProfitabilityData('all');
  } else if (newValue.id) {
    await fetchProjectProfitabilityData(newValue.id);
  } else {
    projectProfitabilityData.value = [];
  }
});

watch(selectedCompanyForProfitability, async (newValue) => {
  if (!newValue) {
    companyProfitabilityData.value = [];
  } else if (newValue.id === 'all') {
    await fetchCompanyProfitabilityData('all');
  } else if (newValue.id) {
    await fetchCompanyProfitabilityData(newValue.id);
  } else {
    companyProfitabilityData.value = [];
  }
});

// Firma karlılık verisi değiştiğinde sayfa numarasını sıfırla
watch(summaryDataAsc, () => {
  currentPageCompanyProfitability.value = 1;
});

// Sayfa başına kayıt sayısı değiştiğinde sayfa numarasını sıfırla
watch(itemsPerPageCompanyProfitability, () => {
  currentPageCompanyProfitability.value = 1;
});

const getPersonelData = async () => {
  try {
    const res = await apiClient.get('/personal/dashboard');
    personalData.value = Array.isArray(res.data) ? res.data.map(p => ({ id: p.id, name: p.name, ...p })) : [];
  } catch (error) { console.error('Personel verileri yüklenirken hata:', error); personalData.value = []; }
};

const getCompaniesList = async () => {
  try {
    const response = await apiClient.get('companies/companies-name');
    companiesList.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) { console.error('Firmalar yüklenirken hata:', error); companiesList.value = []; }
};

const fetchProjectProfitabilityData = async (personalId) => {
  isLoadingProfitabilityData.value = true;
  try {
    let response;
    if (personalId === 'all') {
      response = await apiClient.get(`project-profitability-m/details`);
    } else if (personalId) {
      response = await apiClient.get(`project-profitability-m/details`, { params: { personalId } });
    } else {
      projectProfitabilityData.value = [];
      return;
    }
    projectProfitabilityData.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(`Proje karlılık verisi yüklenirken hata:`, error);
    projectProfitabilityData.value = [];
  } finally { isLoadingProfitabilityData.value = false; }
};

const fetchCompanyProfitabilityData = async (companyId) => {
  isLoadingCompanyProfitability.value = true;
  try {
    let response;
    if (companyId === 'all') {
      response = await apiClient.get(`contract/company-details`);
    } else if (companyId) {
      response = await apiClient.get(`contract/company-details`, { params: { companyId } });
    } else {
      companyProfitabilityData.value = [];
      return;
    }
    companyProfitabilityData.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(`Firma kârlılık verisi yüklenirken hata:`, error);
    companyProfitabilityData.value = [];
  } finally { isLoadingCompanyProfitability.value = false; }
};

const exportToExcel = (dataToExport, headers, totalsRow, baseFileName) => {
  if (!dataToExport.length && !totalsRow) { alert("Dışa aktarılacak veri bulunmamaktadır."); return; }
  const dataForExcel = dataToExport.map(item => {
    const excelRow = {};
    headers.forEach(header => {
      const rawValue = item[header.key];
      let formattedValue = rawValue;
      if (rawValue === null || typeof rawValue === 'undefined') { formattedValue = ""; }
      else if (header.formatter) { formattedValue = header.formatter(rawValue, header.key === 'personal_end_date'); }
      if (["Bilgi Yok", "Geçersiz Sayı", "Geçersiz Değer"].includes(formattedValue)) { formattedValue = ""; }
      excelRow[header.label] = formattedValue;
    });
    return excelRow;
  });
  if (totalsRow) {
    const excelTotalsRow = {};
    headers.forEach(header => {
      const totalValue = totalsRow[header.key];
      if (header.key === headers[0].key) { excelTotalsRow[header.label] = 'TOPLAM'; }
      else if (header.isSummable && totalValue !== undefined && totalValue !== null) {
        excelTotalsRow[header.label] = (header.formatter && typeof totalValue === 'number') ? header.formatter(totalValue) : totalValue;
        if (["Bilgi Yok", "Geçersiz Sayı", "Geçersiz Değer"].includes(excelTotalsRow[header.label])) { excelTotalsRow[header.label] = ""; }
      } else { excelTotalsRow[header.label] = ""; }
    });
    if (dataForExcel.length > 0) dataForExcel.push({});
    dataForExcel.push(excelTotalsRow);
  }
  if (!dataForExcel.length) { alert("Dışa aktarılacak veri bulunmamaktadır."); return; }
  const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, baseFileName.substring(0, 30));
  XLSX.writeFile(workbook, `${baseFileName}.xlsx`);
};

const exportPersonalProfitabilityToExcel = () => {
  let fileName = "personel_karlilik_raporu";
  if (selectedPersonal.value && selectedPersonal.value.name && selectedPersonal.value.id !== 'all') {
    fileName = `personel_karlilik_${selectedPersonal.value.name.replace(/\s+/g, '_')}`;
  }
  exportToExcel(sortedProjectProfitabilityData.value, tableHeadersPersonal.value, personnelProfitabilityTotals.value, fileName);
};

const exportCompanyProfitabilityToExcel = () => {
  let fileName = "firma_karlilik_raporu";
  if (selectedCompanyForProfitability.value && selectedCompanyForProfitability.value.name && selectedCompanyForProfitability.value.id !== 'all') {
    fileName = `firma_karlilik_${selectedCompanyForProfitability.value.name.replace(/\s+/g, '_')}`;
  }
  exportToExcel(sortedCompanyProfitabilityData.value, tableHeadersCompany.value, companyProfitabilityTotals.value, fileName);
};

const exportCompanyProfitabilityRatesToExcel = () => {
  if (!summaryDataAsc.value || summaryDataAsc.value.length === 0) {
    alert("Dışa aktarılacak veri bulunmamaktadır.");
    return;
  }
  
  // Tüm sayfalardaki verileri al (sadece sayfadaki değil)
  const dataForExcel = summaryDataAsc.value.map(item => ({
    'Firma Adı': item.company_name || '',
    'Karlılık Oranı (%)': item.profit_after_admin_percentage 
      ? `${parseFloat(item.profit_after_admin_percentage).toFixed(2)}%` 
      : '0%',
    'Kar/Zarar (TL)': item.total_profit_after_admin 
      ? formatCurrency(item.total_profit_after_admin) 
      : formatCurrency(0)
  }));
  
  const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Firma Karlılık');
  XLSX.writeFile(workbook, 'firma_karlilik_oranlari.xlsx');
};

// --- Existing Contract Data and Pagination ---
const getContractData = async () => {
  try {
    const res = await apiClient.get("contract");
    // Backend'den gelen veriyi normalize et (canlı ortam için)
    contractData.value = Array.isArray(res.data) ? res.data.map(contract => {
      // Yeni bir obje oluştur ve deletedAt'i normalize et
      const normalizedContract = { ...contract };
      
      // deletedAt alanını bul ve normalize et
      const deletedAt = contract.deletedAt || 
                       contract.deleted_at || 
                       contract.deletedat || 
                       contract.contractDeletedAt ||
                       contract['deletedAt'] ||
                       contract['deleted_at'] ||
                       contract['deletedat'] ||
                       contract['contractDeletedAt'];
      
      // Her durumda deletedAt olarak set et
      normalizedContract.deletedAt = deletedAt;
      
      return normalizedContract;
    }) : [];
    console.log('Loaded contracts:', contractData.value.length);
    // Debug: İlk sözleşmenin deletedAt durumunu kontrol et
    if (contractData.value.length > 0) {
      const firstContract = contractData.value[0];
      console.log('İlk sözleşme deletedAt:', firstContract.deletedAt);
      console.log('getDeletedAtValue sonucu:', getDeletedAtValue(firstContract));
    }
  } catch (error) {
    console.error('Sözleşme verileri yüklenirken hata:', error);
    contractData.value = [];
  }
};
// deletedAt alanını bulmak için yardımcı fonksiyon
const getDeletedAtValue = (contract) => {
  if (!contract) return null;
  // Tüm olası alan isimlerini kontrol et
  const deletedAt = contract.deletedAt || 
                   contract.deleted_at || 
                   contract.deletedat || 
                   contract.contractDeletedAt ||
                   contract['deletedAt'] ||
                   contract['deleted_at'] ||
                   contract['deletedat'] ||
                   contract['contractDeletedAt'];
  
  // Eğer hala bulunamadıysa, tüm anahtarları kontrol et (canlı ortam için)
  if (!deletedAt) {
    const keys = Object.keys(contract);
    const deletedAtKey = keys.find(key => 
      key.toLowerCase().includes('deleted') && 
      (key.toLowerCase().includes('at') || key.toLowerCase().includes('date'))
    );
    if (deletedAtKey) {
      return contract[deletedAtKey];
    }
  }
  
  return deletedAt;
};

const activeContractsCount = computed(() => {
  if (!contractData.value || !Array.isArray(contractData.value)) return 0;
  return contractData.value.filter(c => {
    if (!c) return false;
    // deleted_at kolonu null ise aktif
    const deletedAt = getDeletedAtValue(c);
    // deletedAt null, undefined, veya boş string ise aktif
    return deletedAt === null || deletedAt === undefined || deletedAt === '';
  }).length;
});
const passiveContractsCount = computed(() => {
  if (!contractData.value || !Array.isArray(contractData.value)) return 0;
  return contractData.value.filter(c => {
    if (!c) return false;
    // deleted_at kolonu dolu ise pasif
    const deletedAt = getDeletedAtValue(c);
    // deletedAt null değil, undefined değil, ve boş string değilse pasif
    return deletedAt !== null && deletedAt !== undefined && deletedAt !== '';
  }).length;
});
const daysUntilRenewal = (timestamp) => {if (!timestamp) return Infinity;const today = new Date();const renewalDate = new Date(timestamp);return Math.ceil((renewalDate - today) / (1000 * 3600 * 24));};
const sortedContracts = computed(() => {
  if (!contractData.value || !Array.isArray(contractData.value)) return [];
  return [...contractData.value]
      .filter(contract => {
        const renewalDate = contract.renewalcontractdate || contract.renewalContractDate;
        return contract && renewalDate && daysUntilRenewal(renewalDate) > -31;
      })
      .sort((a, b) => {
        const renewalDateA = a.renewalcontractdate || a.renewalContractDate;
        const renewalDateB = b.renewalcontractdate || b.renewalContractDate;
        const daysA = daysUntilRenewal(renewalDateA);
        const daysB = daysUntilRenewal(renewalDateB);
        if (daysA < 90 && daysB >= 90) return -1;
        if (daysA >= 90 && daysB < 90) return 1;
        return daysA - daysB;
      });
});
const paginatedContracts = computed(() => {const start = (currentPage.value - 1) * itemsPerPage.value;const end = start + itemsPerPage.value;return sortedContracts.value.slice(start, end);});
const totalPages = computed(() => Math.ceil(sortedContracts.value.length / itemsPerPage.value));
const getEmployeeWorkTypeCounts = () => {if (!personalData.value || !personalData.value.length) return "0 Tam Zamanlı, 0 Yarı Zamanlı";const fullTime = personalData.value.filter(e => e.workType === true).length;const partTime = personalData.value.filter(e => e.workType === false).length;return `${fullTime} Tam Zamanlı, ${partTime} Yarı Zamanlı`;};
const calculateWorkedHoursByRole = (roleName) => {let totalHours = 0;if (!personalData.value) return `${totalHours} (Saat)`;personalData.value.forEach(e => {if (e.workedHours && e.role && e.role.roleName === roleName) {totalHours += e.workedHours;}});return `${totalHours} (Saat)`;};
const getEmployeeTitleAndWorkedHours = () => {if (!personalData.value || !personalData.value.length) {const zeroState = { employeesCount: '0', totalWorkedHours: '0 (Saat)' };return {'İş Güvenliği Uzmanı': zeroState,'İşyeri Hekimi': zeroState,'Diğer Sağlık Personeli': zeroState,};}const counts = {'İş Güvenliği Uzmanı': `${personalData.value.filter(e => e.role && e.role.roleName === 'İş Güvenliği Uzmanı').length}`,'İşyeri Hekimi': `${personalData.value.filter(e => e.role && e.role.roleName === 'İşyeri Hekimi').length}`,'Diğer Sağlık Personeli': `${personalData.value.filter(e => e.role && e.role.roleName === 'Diğer Sağlık Personeli').length}`,};return {'İş Güvenliği Uzmanı': {employeesCount: counts['İş Güvenliği Uzmanı'],totalWorkedHours: calculateWorkedHoursByRole('İş Güvenliği Uzmanı'),},'İşyeri Hekimi': {employeesCount: counts['İşyeri Hekimi'],totalWorkedHours: calculateWorkedHoursByRole('İşyeri Hekimi'),},'Diğer Sağlık Personeli': {employeesCount: counts['Diğer Sağlık Personeli'],totalWorkedHours: calculateWorkedHoursByRole('Diğer Sağlık Personeli'),},};};
const calculateSafetyExpertsByType = (dangerType) => {if (!personalData.value || !personalData.value.length) return { count: 0, totalWorkedHours: '0 Saat' };const experts = personalData.value.filter(e => e.role && e.role.roleName === 'İş Güvenliği Uzmanı' && e.role.dangerType === dangerType);return {count: experts.length,totalWorkedHours: `${experts.reduce((sum, expert) => sum + (expert.workedHours || 0), 0)} Saat`,};};
const getSafetyExpertsCountsForA = () => calculateSafetyExpertsByType('A');
const getSafetyExpertsCountsForB = () => calculateSafetyExpertsByType('B');
const getSafetyExpertsCountsForC = () => calculateSafetyExpertsByType('C');
const getRemainingDaysText = (timestamp) => {if (!timestamp) return "Bilinmiyor";const diff = daysUntilRenewal(timestamp);if (diff <= -31) return null;if (diff >= 0) return `${diff} gün`;return `Sözleşme Bitmiştir (${Math.abs(diff)} gün önce)`;};
const changePage = (page) => {if (page > 0 && page <= totalPages.value) currentPage.value = page;};
const isExpiringSoon = (timestamp) => {
  if (!timestamp) return false;
  const days = daysUntilRenewal(timestamp);
  return days < 90 && days >= 0;
};
const getStatus = (timestamp) => {
  if (!timestamp) return "Bilinmiyor";
  return new Date(timestamp) < new Date() ? "Pasif" : "Aktif";
};

// --- Company Profitability Summary Pagination Methods ---
const changePageCompanyProfitability = (page) => {
  if (page > 0 && page <= totalPagesCompanyProfitability.value) {
    currentPageCompanyProfitability.value = page;
  }
};

// Personel karlılık tablosu sıralama fonksiyonu
const sortPersonalTable = (columnKey) => {
  if (sortColumnPersonal.value === columnKey) {
    // Aynı sütuna tekrar tıklandığında yönü değiştir
    sortDirectionPersonal.value = sortDirectionPersonal.value === 'asc' ? 'desc' : 'asc';
  } else {
    // Yeni sütuna tıklandığında artan sıralama yap
    sortColumnPersonal.value = columnKey;
    sortDirectionPersonal.value = 'asc';
  }
};

// Sıralama göstergesi için fonksiyon
const getSortIcon = (columnKey) => {
  if (sortColumnPersonal.value !== columnKey) {
    return '↕️'; // Sıralanmamış
  }
  return sortDirectionPersonal.value === 'asc' ? '↑' : '↓';
};

// Firma karlılık tablosu sıralama fonksiyonu
const sortCompanyTable = (columnKey) => {
  if (sortColumnCompany.value === columnKey) {
    // Aynı sütuna tekrar tıklandığında yönü değiştir
    sortDirectionCompany.value = sortDirectionCompany.value === 'asc' ? 'desc' : 'asc';
  } else {
    // Yeni sütuna tıklandığında artan sıralama yap
    sortColumnCompany.value = columnKey;
    sortDirectionCompany.value = 'asc';
  }
};

// Firma karlılık tablosu sıralama göstergesi için fonksiyon
const getSortIconCompany = (columnKey) => {
  if (sortColumnCompany.value !== columnKey) {
    return '↕️'; // Sıralanmamış
  }
  return sortDirectionCompany.value === 'asc' ? '↑' : '↓';
};

const goToFirstPageCompanyProfitability = () => {
  currentPageCompanyProfitability.value = 1;
};

const goToLastPageCompanyProfitability = () => {
  currentPageCompanyProfitability.value = totalPagesCompanyProfitability.value;
};
</script>

<template>
  <br>
  <!-- Swiper Section -->
  <div style="background-color: #F8F9FA; margin: 0 5%;">
    <swiper :slides-per-view="4" :space-between="20" :pagination="{ clickable: true }" :modules="[SwiperPaginationModule, Navigation]" :breakpoints="{ 320: { slidesPerView: 1, spaceBetween: 10 }, 768: { slidesPerView: 2, spaceBetween: 15 }, 1024: { slidesPerView: 4, spaceBetween: 20 } }" class="mySwiper">
      <!-- Swiper Slides -->
      <swiper-slide @click="$router.push({ name: 'ActiveContractPage' })" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #EDEEFC; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 40px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-clipboard-check" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h4 style="margin-top: 50px; font-size: 22px; font-weight: bold; text-align: left;">Aktif Sözleşmeler</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">{{ activeContractsCount }}</span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'PassiveContractPage' })" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #E6F1FD; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 40px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-clipboard-x" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h4 style="margin-top: 50px; font-size: 22px; font-weight: bold; text-align: left;">Pasif Sözleşmeler</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">{{ passiveContractsCount }}</span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'personal' })" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #EDEEFC; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-people" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h4 style="margin-top: 60px; font-size: 22px; font-weight: bold; text-align: left;">Çalışan Sayıları</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">{{ getEmployeeWorkTypeCounts() }}</span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'OccupationalSafetyExpertsPage' })" v-if="getEmployeeTitleAndWorkedHours()['İş Güvenliği Uzmanı'] && getEmployeeTitleAndWorkedHours()['İş Güvenliği Uzmanı'].employeesCount !== '0'" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #E6F1FD; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-shield-lock" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h5 style="margin-top: 70px; font-size: 22px; font-weight: bold; text-align: left;">İş Güvenliği Uzmanı</h5>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getEmployeeTitleAndWorkedHours()['İş Güvenliği Uzmanı'].employeesCount }} Çalışan, {{ getEmployeeTitleAndWorkedHours()['İş Güvenliği Uzmanı'].totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'WorkplacePhysicianPage' })" v-if="getEmployeeTitleAndWorkedHours()['İşyeri Hekimi'] && getEmployeeTitleAndWorkedHours()['İşyeri Hekimi'].employeesCount !== '0'" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #EDEEFC; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-heart-pulse" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h4 style="margin-top: 70px; font-size: 22px; font-weight: bold; text-align: left;">İşyeri Hekimi</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getEmployeeTitleAndWorkedHours()['İşyeri Hekimi'].employeesCount }} Çalışan, {{ getEmployeeTitleAndWorkedHours()['İşyeri Hekimi'].totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'OtherHealthPersonnelPage' })" v-if="getEmployeeTitleAndWorkedHours()['Diğer Sağlık Personeli'] && getEmployeeTitleAndWorkedHours()['Diğer Sağlık Personeli'].employeesCount !== '0'" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #E6F1FD; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <i class="bi bi-capsule" style="font-size: 3rem; position: absolute; top: 10px; left: 30px;"></i>
          <h4 style="margin-top: 70px; font-size: 22px; font-weight: bold; text-align: left;">Diğer Sağlık Personeli</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getEmployeeTitleAndWorkedHours()['Diğer Sağlık Personeli'].employeesCount }} Çalışan, {{ getEmployeeTitleAndWorkedHours()['Diğer Sağlık Personeli'].totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'OccupationalSafetyExpertsAPage' })" v-if="getSafetyExpertsCountsForA().count > 0" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #EDEEFC; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <div style="font-size: 3rem; font-weight: bold; position: absolute; top: 5px; left: 30px; color: #67748e;">A</div>
          <h4 style="margin-top: 50px; font-size: 22px; font-weight: bold; text-align: left;">İş Güvenliği Uzmanı : A Sınıfı</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getSafetyExpertsCountsForA().count }} İş Güvenliği Uzmanı, {{ getSafetyExpertsCountsForA().totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'OccupationalSafetyExpertsBPage' })" v-if="getSafetyExpertsCountsForB().count > 0" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #E6F1FD; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <div style="font-size: 3rem; font-weight: bold; position: absolute; top: 5px; left: 30px; color: #67748e;">B</div>
          <h4 style="margin-top: 50px; font-size: 22px; font-weight: bold; text-align: left;">İş Güvenliği Uzmanı : B Sınıfı</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getSafetyExpertsCountsForB().count }} İş Güvenliği Uzmanı, {{ getSafetyExpertsCountsForB().totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide @click="$router.push({ name: 'OccupationalSafetyExpertsCPage' })" v-if="getSafetyExpertsCountsForC().count > 0" style="display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
        <div style="background-color: #EDEEFC; cursor: pointer; width: 300px; height: 220px; text-align: left; align-content: center; padding-top: 20px; padding-left: 30px; display: flex; justify-content: flex-start; align-items: flex-start; flex-direction: column; position: relative; border-radius: 30px;">
          <div style="font-size: 3rem; font-weight: bold; position: absolute; top: 5px; left: 30px; color: #67748e;">C</div>
          <h4 style="margin-top: 50px; font-size: 22px; font-weight: bold; text-align: left;">İş Güvenliği Uzmanı : C Sınıfı</h4>
          <span style="font-size: 22px; font-weight: bold; color: #1C1C1C; text-align: left;">
            {{ getSafetyExpertsCountsForC().count }} İş Güvenliği Uzmanı, {{ getSafetyExpertsCountsForC().totalWorkedHours }}
          </span>
        </div>
      </swiper-slide>
      <swiper-slide v-if="summaryData.total_contract_price !== undefined">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Toplam Sözleşme Tutarı</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(summaryData.total_contract_price) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide>
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Aktif Sözleşme Tutarı</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(activeContractsAmount) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide v-if="totalCostData.totalCost !== undefined">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Toplam Maliyet</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(totalCostData.totalCost) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide>
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Yönetim Gideri Öncesi Kâr (TL)</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(profitBeforeManagementAmount) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide v-if="activeContractsAmount !== undefined">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Yönetim Gideri Öncesi Kar Oranı</div>
          <div style="font-size: 1.5rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ (profitBeforeManagementRate * 100).toFixed(2) }}%</div>
        </div>
      </swiper-slide>
      <swiper-slide v-if="latestManagementExpense">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Artı Yönetim Gideri Payı (TL)</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(latestManagementExpense.companyManagementExpense) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide v-if="activeContractsAmount !== undefined && totalCostData.totalCost !== undefined && latestManagementExpense">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Yönetim Gideri Sonrası Kar (TL)</div>
          <span style="font-size: 2rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ formatCurrency(profitAfterManagementAmount) }}</span>
        </div>
      </swiper-slide>
      <swiper-slide v-if="activeContractsAmount !== undefined && totalCostData.totalCost !== undefined && latestManagementExpense">
        <div style="background-color: #EDEEFC; width: 300px; height: 220px; text-align: left; align-content: center; padding: 20px 30px; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; border-radius: 30px;">
          <div style="font-size: 1rem; font-weight: bold; color: #67748e;">Yönetim Gideri Sonrası Kar Oranı</div>
          <div style="font-size: 1.5rem; font-weight: bold; color: #1C1C1C; margin-top: 10px;">{{ (profitAfterManagementRate * 100).toFixed(2) }}%</div>
        </div>
      </swiper-slide>

      <br><br>
    </swiper>
  </div>

  <!-- Personnel Project Profitability Section -->
  <div class="dashboard" style="margin: 0 5%;">
    <h4 class="mt-4 mb-3">Personel Bazında Proje Karlılık Raporu</h4>
    <div class="row mb-3 align-items-center">
      <div class="col-md-6">
        <div class="searchable-select-wrapper">
          <input
              type="text"
              v-model="personalSearchQuery"
              class="form-control"
              placeholder="Personel Seçin veya Arayın..."
              @focus="isPersonalDropdownOpen = true"
              @blur="closeDropdown('personal')"
              @input="isPersonalDropdownOpen = true; selectedPersonal = null"
          />
          <ul v-if="isPersonalDropdownOpen" class="searchable-select-dropdown">
            <li @click="selectPersonal({ id: 'all', name: 'Tümü' })">
              <strong>Tümü</strong>
            </li>
            <li v-for="person in filteredPersonalData" :key="person.id" @click="selectPersonal(person)">
              {{ person.name }}
            </li>
            <li v-if="filteredPersonalData.length === 0 && personalSearchQuery">
              Sonuç bulunamadı.
            </li>
          </ul>
        </div>
      </div>
      <div class="col-md-6 text-md-end mt-2 mt-md-0">
        <button @click="exportPersonalProfitabilityToExcel" class="btn btn-success" :disabled="(!projectProfitabilityData.length && !personnelProfitabilityTotals) || isLoadingProfitabilityData">
          <i class="bi bi-file-earmark-excel"></i> Excel'e Aktar
        </button>
      </div>
    </div>

    <!-- Personnel Profitability Table -->
    <div v-if="isLoadingProfitabilityData" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Yükleniyor...</span></div>
      <p>Veriler yükleniyor...</p>
    </div>
    <div v-else-if="!selectedPersonal" class="alert alert-info text-center">Lütfen bir personel seçin veya tüm personelleri görüntülemek için seçim yapın.</div>
    <div v-else-if="!projectProfitabilityData.length" class="alert alert-warning text-center">Seçilen kriter için gösterilecek proje karlılık verisi bulunmamaktadır.</div>
    <div v-else class="table-responsive-wrapper">
      <table class="table table-striped table-bordered table-hover">
        <thead class="table-dark">
        <tr>
          <th 
            v-for="header in tableHeadersPersonal" 
            :key="header.key"
            @click="sortPersonalTable(header.key)"
            :style="{ cursor: 'pointer', userSelect: 'none' }"
            class="sortable-header"
          >
            {{ header.label }} {{ getSortIcon(header.key) }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in sortedProjectProfitabilityData" :key="item.project_profitability_id || `personal-profit-${index}`">
          <td v-for="header in tableHeadersPersonal" :key="header.key" :style="{textAlign: header.formatter === formatCurrency || (typeof item[header.key] === 'number' && !header.formatter) ? 'right' : 'left'}">
            {{ header.formatter ? header.formatter(item[header.key]) : (item[header.key] === null || typeof item[header.key] === 'undefined' ? (header.isSummable ? '0' : 'Bilgi Yok') : item[header.key]) }}
          </td>
        </tr>
        </tbody>
        <tfoot v-if="personnelProfitabilityTotals" class="table-totals">
        <tr>
          <td v-for="header in tableHeadersPersonal" :key="`total-p-${header.key}`" :style="{textAlign: header.formatter === formatCurrency || (header.isSummable && typeof personnelProfitabilityTotals[header.key] === 'number') ? 'right' : 'left'}">
            <span v-if="personnelProfitabilityTotals[header.key] !== undefined">
              {{ header.formatter && typeof personnelProfitabilityTotals[header.key] === 'number' && header.key !== tableHeadersPersonal[0].key ? header.formatter(personnelProfitabilityTotals[header.key]) : personnelProfitabilityTotals[header.key] }}
            </span>
          </td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>

  <hr class="my-5" style="border-top: 2px solid #ccc;">

  <!-- Company Project Profitability Section -->
  <div class="dashboard" style="margin: 0 5%;">
    <h4 class="mt-4 mb-3">Firma Bazında Proje Karlılık Raporu</h4>
    <div class="row mb-3 align-items-center">
      <div class="col-md-6">
        <div class="searchable-select-wrapper">
          <input
              type="text"
              v-model="companySearchQuery"
              class="form-control"
              placeholder="Firma Seçin veya Arayın..."
              @focus="isCompanyDropdownOpen = true"
              @blur="closeDropdown('company')"
              @input="isCompanyDropdownOpen = true; selectedCompanyForProfitability = null"
          />
          <ul v-if="isCompanyDropdownOpen" class="searchable-select-dropdown">
            <li @click="selectCompany({ id: 'all', name: 'Tümü' })">
              <strong>Tümü</strong>
            </li>
            <li v-for="company in filteredCompaniesList" :key="company.id" @click="selectCompany(company)">
              {{ company.name }}
            </li>
            <li v-if="filteredCompaniesList.length === 0 && companySearchQuery">
              Sonuç bulunamadı.
            </li>
          </ul>
        </div>
      </div>
      <div class="col-md-6 text-md-end mt-2 mt-md-0">
        <button @click="exportCompanyProfitabilityToExcel" class="btn btn-success" :disabled="(!companyProfitabilityData.length && !companyProfitabilityTotals) || isLoadingCompanyProfitability">
          <i class="bi bi-file-earmark-excel"></i> Excel'e Aktar
        </button>
      </div>
    </div>

    <!-- Company Profitability Table -->
    <div v-if="isLoadingCompanyProfitability" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Yükleniyor...</span></div>
      <p>Firma verileri yükleniyor...</p>
    </div>
    <div v-else-if="!selectedCompanyForProfitability" class="alert alert-info text-center">Lütfen bir firma seçin veya tüm firmaları görüntülemek için seçim yapın.</div>
    <div v-else-if="!companyProfitabilityData.length" class="alert alert-warning text-center">Seçilen kriter için gösterilecek proje karlılık verisi bulunmamaktadır.</div>
    <div v-else class="table-responsive-wrapper">
      <table class="table table-striped table-bordered table-hover">
        <thead class="table-dark">
        <tr>
          <th 
            v-for="header in tableHeadersCompany" 
            :key="header.key"
            @click="sortCompanyTable(header.key)"
            :style="{ cursor: 'pointer', userSelect: 'none' }"
            class="sortable-header"
          >
            {{ header.label }} {{ getSortIconCompany(header.key) }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in sortedCompanyProfitabilityData" :key="item.project_profitability_id || item.contract_id || `company-profit-${index}`">
          <td v-for="header in tableHeadersCompany" :key="header.key" :style="{textAlign: header.formatter === formatCurrency || (typeof item[header.key] === 'number' && !header.formatter) ? 'right' : 'left'}">
            {{ header.formatter ? header.formatter(item[header.key]) : (item[header.key] === null || typeof item[header.key] === 'undefined' ? (header.isSummable ? '0' : 'Bilgi Yok') : item[header.key]) }}
          </td>
        </tr>
        </tbody>
        <tfoot v-if="companyProfitabilityTotals" class="table-totals">
        <tr>
          <td v-for="header in tableHeadersCompany" :key="`total-c-${header.key}`" :style="{textAlign: header.formatter === formatCurrency || (header.isSummable && typeof companyProfitabilityTotals[header.key] === 'number') ? 'right' : 'left'}">
             <span v-if="companyProfitabilityTotals[header.key] !== undefined">
              {{ header.formatter && typeof companyProfitabilityTotals[header.key] === 'number' && header.key !== tableHeadersCompany[0].key ? header.formatter(companyProfitabilityTotals[header.key]) : companyProfitabilityTotals[header.key] }}
            </span>
          </td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>

  <br>

  <!-- Firma Karlılık Özet Tablosu -->
  <div style="margin: 0 5%;">
    <div class="row mt-4">
      <div class="col-lg-12 mb-lg-0 mb-4" style="background-color: #F9F9FA !important;">
        <div class="card" style="background-color: #F9F9FA !important;">
          <div class="p-3 pb-0 card-header" style="background-color: #F9F9FA !important;">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-2">Firma Karlılık Oranları</h6>
                <small class="text-muted">
                  <i class="bi bi-sort-numeric-down"></i>
                  En yüksek karlılık oranından düşüğe doğru sıralanmıştır
                </small>
              </div>
              <div class="d-flex align-items-center gap-2">
                <label for="itemsPerPageSelect" class="mb-0" style="font-size: 0.875rem;">Sayfa başına:</label>
                <select 
                  id="itemsPerPageSelect"
                  v-model="itemsPerPageCompanyProfitability" 
                  class="form-select form-select-sm" 
                  style="width: auto; min-width: 70px;"
                >
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="50">50</option>
                </select>
                <button 
                  @click="exportCompanyProfitabilityRatesToExcel" 
                  class="btn btn-success" 
                  :disabled="!summaryDataAsc || summaryDataAsc.length === 0"
                  style="background-color: #74DDB0; border-color: #74DDB0; color: #575757;"
                >
                  <i class="bi bi-file-earmark-excel"></i> Excel'e Aktar
                </button>
              </div>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
              <tr>
                <th class="text-center">Firma Adı</th>
                <th 
                  class="text-center sortable-header" 
                  @click="sortCompanyRatesTable('profit_after_admin_percentage')"
                  :style="{ cursor: 'pointer', userSelect: 'none' }"
                >
                  Karlılık Oranı (%) {{ getSortIconCompanyRates('profit_after_admin_percentage') }}
                </th>
                <th 
                  class="text-center sortable-header" 
                  @click="sortCompanyRatesTable('total_profit_after_admin')"
                  :style="{ cursor: 'pointer', userSelect: 'none' }"
                >
                  Kar/Zarar (TL) {{ getSortIconCompanyRates('total_profit_after_admin') }}
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in paginatedCompanyProfitability" :key="index" :class="getProfitabilityClass(item.profit_after_admin_percentage)">
                <td class="text-center">{{ item.company_name }}</td>
                <td class="text-center">{{ formatPercentage(item.profit_after_admin_percentage) }}</td>
                <td class="text-center">{{ formatCurrency(item.total_profit_after_admin || 0) }}</td>
              </tr>
              <tr v-if="!paginatedCompanyProfitability.length">
                <td colspan="3" class="text-center">Gösterilecek veri bulunmamaktadır.</td>
              </tr>
              </tbody>
            </table>

            <!-- Sayfalama -->
            <div v-if="totalPagesCompanyProfitability > 1" class="d-flex justify-content-center align-items-center mt-4">
              <nav aria-label="Firma Karlılık Oranları Sayfalama">
                <ul class="pagination pagination-sm mb-0">
                  <!-- İlk Sayfa -->
                  <li class="page-item" :class="{ disabled: !hasPrevPageCompanyProfitability }">
                    <button class="page-link" @click="goToFirstPageCompanyProfitability" :disabled="!hasPrevPageCompanyProfitability" title="İlk Sayfa">
                      <i class="bi bi-chevron-double-left"></i>
                    </button>
                  </li>

                  <!-- Önceki Sayfa -->
                  <li class="page-item" :class="{ disabled: !hasPrevPageCompanyProfitability }">
                    <button class="page-link" @click="changePageCompanyProfitability(currentPageCompanyProfitability - 1)" :disabled="!hasPrevPageCompanyProfitability" title="Önceki Sayfa">
                      <i class="bi bi-chevron-left"></i>
                    </button>
                  </li>

                  <!-- Sayfa Numaraları -->
                  <li class="page-item active">
                    <span class="page-link page-number">{{ currentPageCompanyProfitability }} / {{ totalPagesCompanyProfitability }}</span>
                  </li>

                  <!-- Sonraki Sayfa -->
                  <li class="page-item" :class="{ disabled: !hasNextPageCompanyProfitability }">
                    <button class="page-link" @click="changePageCompanyProfitability(currentPageCompanyProfitability + 1)" :disabled="!hasNextPageCompanyProfitability" title="Sonraki Sayfa">
                      <i class="bi bi-chevron-right"></i>
                    </button>
                  </li>

                  <!-- Son Sayfa -->
                  <li class="page-item" :class="{ disabled: !hasNextPageCompanyProfitability }">
                    <button class="page-link" @click="goToLastPageCompanyProfitability" :disabled="!hasNextPageCompanyProfitability" title="Son Sayfa">
                      <i class="bi bi-chevron-double-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>

            <!-- Sayfa Bilgisi -->
            <div v-if="summaryDataAsc.length > 0" class="text-center mt-3 mb-2">
              <div class="page-info">
                <span class="page-info-text">
                  Toplam <strong>{{ summaryDataAsc.length }}</strong> firma •
                  Sayfa <strong>{{ currentPageCompanyProfitability }}</strong> / <strong>{{ totalPagesCompanyProfitability }}</strong> •
                  Sayfa başına <strong>{{ itemsPerPageCompanyProfitability }}</strong> firma
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <br>

  <!-- Existing Contracts Table -->
  <div class="dashboard" style="margin: 0 5%;">
    <div class="row mt-4">
      <div class="col-lg-12 mb-lg-0 mb-4" style="background-color: #F9F9FA !important;">
        <div class="card" style="background-color: #F9F9FA !important;">
          <div class="p-3 pb-0 card-header" style="background-color: #F9F9FA !important;">
            <div class="d-flex justify-content-between" style="background-color: #F9F9FA !important;">
              <h6 class="mb-2">Sözleşme Bitiş Tarihi Yaklaşanlar</h6>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table">
              <thead>
              <tr>
                <th>Firma Adı</th>
                <th>Sözleşme Numarası</th>
                <th>Sözleşme Adı</th>
                <th>Yenilenme Tarihi</th>
                <th>Kalan Gün</th>
                <th>Durum</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="paginatedContracts.length === 0">
                <td colspan="6" style="text-align: center;">Gösterilecek sözleşme bulunmamaktadır.</td>
              </tr>
              <tr v-for="(contract) in paginatedContracts" :key="contract.id" :class="{'expiring-soon': isExpiringSoon(contract.renewalcontractdate || contract.renewalContractDate)}">
                <td>{{ contract.companyname || contract.companyName || 'Bilgi Yok' }}</td>
                <td>{{ contract.contractnumber || contract.contractNumber || 'Bilgi Yok' }}</td>
                <td>
                  <router-link :to="{ name: 'contractDetails', params: { id: contract.id } }">{{ contract.contractname || contract.contractName || 'Bilgi Yok' }}</router-link>
                </td>
                <td>{{ formatDate(contract.renewalcontractdate || contract.renewalContractDate) }}</td>
                <td>{{ getRemainingDaysText(contract.renewalcontractdate || contract.renewalContractDate) }}</td>
                <td><span :class="getStatus(contract.renewalcontractdate || contract.renewalContractDate) === 'Aktif' ? 'status-active' : 'status-passive'">{{ getStatus(contract.renewalcontractdate || contract.renewalContractDate) }}</span></td>
              </tr>
              </tbody>
            </table>
            <div class="pagination" v-if="totalPages > 1">
              <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1"><</button>
              <span>Sayfa {{ currentPage }} / {{ totalPages }}</span>
              <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">></button>
            </div>
            <br><br>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mySwiper {
  width: 100%;
  padding-bottom: 40px;
}
.swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 5px;
}
.swiper-slide > div {
  width: 100%;
  max-width: 300px;
  min-height: 220px;
  box-sizing: border-box;
}
.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  padding: 15px;
}
.table-responsive-wrapper table {
  min-width: 2200px;
  border-collapse: collapse;
}
.table-responsive-wrapper table th,
.table-responsive-wrapper table td {
  white-space: nowrap;
  padding: 0.65rem 0.75rem;
  border: 1px solid #dee2e6;
  vertical-align: middle;
}
.table-responsive-wrapper table thead.table-dark th {
  color: #000000 !important;
  background-color: #e9ecef !important;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 10;
}
.table-responsive-wrapper table tfoot.table-totals td {
  font-weight: bold;
  background-color: #f0f2f5;
  border-top: 2px solid #ced4da;
  position: sticky;
  bottom: 0;
  z-index: 10;
}
.dashboard .table-responsive .table th {
  background-color: #f8f9fa;
  font-weight: bold;
  text-align: center;
  border: 1px solid #dee2e6;
  vertical-align: middle;
}
.dashboard .table-responsive .table td {
  border: 1px solid #dee2e6;
  vertical-align: middle;
  text-align: center;
}
.expiring-soon {
  background-color: #ffebee !important;
  color: #c62828 !important;
  font-weight: bold;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
  margin-bottom: 15px;
}
.pagination button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  margin: 0 5px;
  cursor: pointer;
  border-radius: 5px;
  transition: background-color 0.2s;
}
.pagination button:hover:not(:disabled) {
  background-color: #0056b3;
}
.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.status-active {
  color: #2e7d32;
  font-weight: bold;
}
.status-passive {
  color: #c62828;
  font-weight: bold;
}
:global(.swiper-pagination-bullet-active) {
  background-color: #007bff !important;
}
@media (max-width: 767px) {
  .swiper-slide > div {
    height: auto;
    min-height: 180px;
    padding-left: 20px;
    padding-top: 30px;
  }
  .swiper-slide h4, .swiper-slide h5 {
    font-size: 18px;
    margin-top: 40px;
  }
  .swiper-slide span {
    font-size: 18px;
  }
  .swiper-slide i {
    font-size: 2.5rem;
  }
  .swiper-slide div[style*="font-size: 3rem;"] {
    font-size: 2.5rem !important;
  }
  .dashboard .row .col-md-6 {
    text-align: center !important;
  }
  .dashboard .row .col-md-6.text-md-end {
    margin-top: 0.5rem;
  }
  .table-responsive-wrapper table {
    min-width: 100%;
  }
}
.low-profit {
  background-color: #ffebee !important;
  color: #c62828;
}
.medium-profit {
  background-color: #fff3e0 !important;
  color: #ef6c00;
}
.high-profit {
  background-color: #e8f5e9 !important;
  color: #2e7d32;
}
@media (max-width: 767px) {
  .table td, .table th {
    padding: 0.5rem;
    font-size: 14px;
  }
}

/* YENİ: Arama yapılabilen özel select kutusu için stiller */
.searchable-select-wrapper {
  position: relative;
  width: 100%;
}
.searchable-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 250px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.searchable-select-dropdown li {
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.searchable-select-dropdown li:hover {
  background-color: #f0f2f5;
}
.searchable-select-dropdown li:last-child {
  border-bottom: none;
}

/* YENİ: Firma Karlılık Oranları sayfalama stilleri */
.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

/* YENİ: Sayfa bilgisi stilleri */
.page-info {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 20px;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.page-info-text {
  color: #495057;
  font-size: 14px;
  font-weight: 500;
}

.page-info-text strong {
  color: #007bff;
  font-weight: 600;
}

/* Sayfa numarası için özel stil */
.page-number {
  font-weight: 600 !important;
  min-width: 60px;
  text-align: center;
}

/* Responsive tasarım için */
@media (max-width: 576px) {
  .page-info {
    padding: 8px 12px;
  }

  .page-info-text {
    font-size: 12px;
  }

  .pagination-sm .page-link {
    padding: 0.2rem 0.4rem;
    font-size: 0.8rem;
  }
}

.pagination-sm .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.pagination-sm .page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

.pagination-sm .page-link:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
  color: #495057;
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.pagination-sm .page-item:not(.disabled) .page-link:hover {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
  box-shadow: 0 2px 4px rgba(0,123,255,0.3);
}

.pagination-sm .page-link:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Sıralanabilir başlık stilleri */
.sortable-header {
  position: relative;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #d0d3d8 !important;
}

.sortable-header:active {
  background-color: #b8bcc2 !important;
}
</style>
